// EIP-712 domains and types, mirrored from the Solidity typehashes. The e2e tests sign with these and the
// contracts accept the signatures, which keeps both sides in sync.
import { encodeFunctionData, type Address, type Hex } from 'viem';

import { burnAuctionAbi, exchangeAbi, sessionKeyManagerAbi, stakingAbi } from './abi.ts';
import type { Addresses } from './config.ts';

export type OpenRequest = {
  user: Address;
  instrumentId: number;
  isLong: boolean;
  margin: bigint;
  leverage: number;
  /** Worst fill in oracle units (a long at most, a short at least); 0 for any price */
  acceptablePx: bigint;
  nonce: bigint;
  deadline: bigint;
};
/** `limitPx[i]`: worst exit of `positionIds[i]` (0 = any price); empty closes everything at any price */
export type CloseRequest = { user: Address; positionIds: bigint[]; limitPx: bigint[]; nonce: bigint; deadline: bigint };
export type AmountRequest = { user: Address; amount: bigint; nonce: bigint; deadline: bigint };
/** A sale into the burn auction: `minTick` is the reserve (PapurrBurnAuction.tickPrice; 0 = any price); `amount` 0 only moves it */
export type SellRequest = AmountRequest & { minTick: number };
export type ClaimRequest = { user: Address; nonce: bigint; deadline: bigint };
export type RegisterRequest = { user: Address; key: Address; expiry: bigint; nonce: bigint; deadline: bigint };

export type Intent =
  | { action: 'open'; request: OpenRequest }
  | { action: 'close'; request: CloseRequest }
  | { action: 'withdrawToCore'; request: AmountRequest }
  | { action: 'stake'; request: AmountRequest }
  | { action: 'unstake'; request: AmountRequest }
  | { action: 'claim'; request: ClaimRequest }
  | { action: 'claimToEvm'; request: ClaimRequest }
  | { action: 'auctionSell'; request: SellRequest }
  | { action: 'auctionClaim'; request: ClaimRequest }
  | { action: 'auctionClaimToEvm'; request: ClaimRequest }
  | { action: 'registerSessionKey'; request: RegisterRequest };

export type Action = Intent['action'];
export const ACTIONS: readonly Action[] = [
  'open',
  'close',
  'withdrawToCore',
  'stake',
  'unstake',
  'claim',
  'claimToEvm',
  'auctionSell',
  'auctionClaim',
  'auctionClaimToEvm',
  'registerSessionKey',
];

export const types = {
  OpenPosition: [
    { name: 'user', type: 'address' },
    { name: 'instrumentId', type: 'uint32' },
    { name: 'isLong', type: 'bool' },
    { name: 'margin', type: 'uint128' },
    { name: 'leverage', type: 'uint32' },
    { name: 'acceptablePx', type: 'uint64' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint64' },
  ],
  Close: [
    { name: 'user', type: 'address' },
    { name: 'positionIds', type: 'uint256[]' },
    { name: 'limitPx', type: 'uint64[]' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint64' },
  ],
  WithdrawToCore: [
    { name: 'user', type: 'address' },
    { name: 'amount', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint64' },
  ],
  Stake: [
    { name: 'user', type: 'address' },
    { name: 'amount', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint64' },
  ],
  Unstake: [
    { name: 'user', type: 'address' },
    { name: 'amount', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint64' },
  ],
  Claim: [
    { name: 'user', type: 'address' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint64' },
  ],
  // a claim paid as native HYPE to the user on HyperEVM (no HyperCore account needed), its own type so a
  // signature for one destination never serves the other
  ClaimToEvm: [
    { name: 'user', type: 'address' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint64' },
  ],
  Sell: [
    { name: 'user', type: 'address' },
    { name: 'amount', type: 'uint256' },
    { name: 'minTick', type: 'uint32' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint64' },
  ],
  RegisterSessionKey: [
    { name: 'user', type: 'address' },
    { name: 'key', type: 'address' },
    { name: 'expiry', type: 'uint64' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint64' },
  ],
} as const;

const PRIMARY: Record<Action, keyof typeof types> = {
  open: 'OpenPosition',
  close: 'Close',
  withdrawToCore: 'WithdrawToCore',
  stake: 'Stake',
  unstake: 'Unstake',
  claim: 'Claim',
  claimToEvm: 'ClaimToEvm',
  auctionSell: 'Sell',
  auctionClaim: 'Claim',
  auctionClaimToEvm: 'ClaimToEvm',
  registerSessionKey: 'RegisterSessionKey',
};

const STAKING_ACTIONS: readonly Action[] = ['stake', 'unstake', 'claim', 'claimToEvm'];
const AUCTION_ACTIONS: readonly Action[] = ['auctionSell', 'auctionClaim', 'auctionClaimToEvm'];

/** Contract that verifies (and executes) the intent, and whose domain the signature is bound to. */
export function verifyingContract(action: Action, a: Addresses): Address {
  if (STAKING_ACTIONS.includes(action)) return a.staking;
  if (AUCTION_ACTIONS.includes(action)) {
    if (!a.burnAuction) throw new Error('this deployment has no burn auction');
    return a.burnAuction;
  }
  if (action === 'registerSessionKey') return a.sessionKeyManager;
  return a.exchange;
}

function domainName(action: Action): string {
  if (STAKING_ACTIONS.includes(action)) return 'PapurrStaking';
  if (AUCTION_ACTIONS.includes(action)) return 'PapurrBurnAuction';
  if (action === 'registerSessionKey') return 'SessionKeyManager';
  return 'PapurrExchange';
}

/**
 * Typed data of a request to drop a pending intent from the relayer queue, signed by its user or one of
 * its session keys. No contract verifies this type: it only reaches the relayer, and once the intent is
 * executing it is too late.
 */
export function cancelTypedData(intentId: Hex, a: Addresses, chainId: number) {
  return {
    domain: { name: 'PapurrRelayer', version: '1', chainId, verifyingContract: a.exchange },
    types: { CancelIntent: [{ name: 'intentId', type: 'bytes32' }] },
    primaryType: 'CancelIntent',
    message: { intentId },
  } as const;
}

/**
 * Typed data of a report that the user burned USDC on another chain (Circle CCTP V2) for the keeper to
 * finalize here, signed by the user or one of its session keys: without it anyone could fill a user's
 * in-flight quota with hashes that never attest.
 */
export function cctpReportTypedData(sourceDomain: number, txHash: Hex, a: Addresses, chainId: number) {
  return {
    domain: { name: 'PapurrRelayer', version: '1', chainId, verifyingContract: a.exchange },
    types: {
      ReportCctpDeposit: [
        { name: 'sourceDomain', type: 'uint32' },
        { name: 'txHash', type: 'bytes32' },
      ],
    },
    primaryType: 'ReportCctpDeposit',
    message: { sourceDomain, txHash },
  } as const;
}

/** Typed data to sign (wallet) or verify (relayer) for an intent. */
export function typedData(intent: Intent, a: Addresses, chainId: number) {
  return {
    domain: { name: domainName(intent.action), version: '1', chainId, verifyingContract: verifyingContract(intent.action, a) },
    types,
    primaryType: PRIMARY[intent.action],
    message: intent.request,
  } as const;
}

/** Calldata of the `xxxBySig` call executing a signed intent. */
export function encodeCall(intent: Intent, signature: Hex, a: Addresses): { target: Address; data: Hex } {
  const target = verifyingContract(intent.action, a);
  switch (intent.action) {
    case 'open':
      return { target, data: encodeFunctionData({ abi: exchangeAbi, functionName: 'openPositionBySig', args: [intent.request, signature] }) };
    case 'close':
      return { target, data: encodeFunctionData({ abi: exchangeAbi, functionName: 'closeBySig', args: [intent.request, signature] }) };
    case 'withdrawToCore':
      return { target, data: encodeFunctionData({ abi: exchangeAbi, functionName: 'withdrawToCoreBySig', args: [intent.request, signature] }) };
    case 'stake':
    case 'unstake': {
      const r = intent.request;
      const functionName = intent.action === 'stake' ? 'stakeBySig' : 'unstakeBySig';
      return { target, data: encodeFunctionData({ abi: stakingAbi, functionName, args: [r.user, r.amount, r.nonce, r.deadline, signature] }) };
    }
    case 'claim':
    case 'claimToEvm': {
      const r = intent.request;
      const functionName = intent.action === 'claim' ? 'claimBySig' : 'claimToEvmBySig';
      return { target, data: encodeFunctionData({ abi: stakingAbi, functionName, args: [r.user, r.nonce, r.deadline, signature] }) };
    }
    case 'auctionSell': {
      const r = intent.request;
      return { target, data: encodeFunctionData({ abi: burnAuctionAbi, functionName: 'sellBySig', args: [r.user, r.amount, r.minTick, r.nonce, r.deadline, signature] }) };
    }
    case 'auctionClaim':
    case 'auctionClaimToEvm': {
      const r = intent.request;
      const functionName = intent.action === 'auctionClaim' ? 'claimBySig' : 'claimToEvmBySig';
      return { target, data: encodeFunctionData({ abi: burnAuctionAbi, functionName, args: [r.user, r.nonce, r.deadline, signature] }) };
    }
    case 'registerSessionKey': {
      const r = intent.request;
      return {
        target,
        data: encodeFunctionData({
          abi: sessionKeyManagerAbi,
          functionName: 'registerSessionKeyBySig',
          args: [r.user, r.key, r.expiry, r.nonce, r.deadline, signature],
        }),
      };
    }
  }
}
